// src/ui/SecurityCard.jsx — the web's "a new device signed in" card.
//
// The phone writes this into your own Note to Self after every approved login
// (allo-app/services/SecurityAlerts.ts). The payload is JSON sealed in the text field,
// so by the time it reaches here the ordinary per-device decrypt has already turned it
// back into a string.
//
// 🟥 THE MAP IS PLAIN OPENSTREETMAP RASTER TILES, exactly as on the phone — no map SDK,
//    no API key, no billing. The licence asks for attribution, which is the
//    "© OpenStreetMap" line in the corner; do not remove it.

import React, { useMemo, useState } from 'react';
import { ShieldCheck, Laptop, MapPin, KeyRound, Alert } from './icons';

const MAP_H = 128;
const TILE = 256;
const ZOOM = 12;                     // town-level: recognisable, never pinpoints a house

const lonToX = (lon, z) => ((lon + 180) / 360) * Math.pow(2, z);
const latToY = (lat, z) => {
  const r = (lat * Math.PI) / 180;
  return ((1 - Math.log(Math.tan(r) + 1 / Math.cos(r)) / Math.PI) / 2) * Math.pow(2, z);
};

/** The tiles needed to fill w × MAP_H with the point dead centre. */
function tilesFor(lat, lon, w) {
  const n = Math.pow(2, ZOOM);
  const left = lonToX(lon, ZOOM) * TILE - w / 2;
  const top = latToY(lat, ZOOM) * TILE - MAP_H / 2;
  const out = [];
  for (let x = Math.floor(left / TILE); x <= Math.floor((left + w - 1) / TILE); x++) {
    for (let y = Math.floor(top / TILE); y <= Math.floor((top + MAP_H - 1) / TILE); y++) {
      if (y < 0 || y >= n) continue;                  // above the pole / below it
      const wx = ((x % n) + n) % n;                   // the world wraps east-west
      out.push({
        key: `${x}_${y}`,
        uri: `https://tile.openstreetmap.org/${ZOOM}/${wx}/${y}.png`,
        left: x * TILE - left,
        top: y * TILE - top,
      });
    }
  }
  return out;
}

const METHOD_LINE = {
  device_approval: 'Approved from one of your devices',
  qr: 'Paired by scanning your QR code',
  email_code: 'Verified with a code sent to your email',
  first_device: 'First device on this account',
};

export function parseSecurity(text) {
  if (!text || typeof text !== 'string') return null;
  const s = text.trim();
  if (!s.startsWith('{')) return null;      // still sealed, or a legacy row
  try {
    const p = JSON.parse(s);
    return p && p.kind === 'login' ? p : null;
  } catch { return null; }
}

export function securityPreview(text) {
  const p = parseSecurity(text);
  if (!p) return '🔐 New login detected';
  const where = [p.city, p.region || p.country].filter(Boolean).join(', ');
  return `🔐 New login — ${p.deviceName || 'a new device'}${where ? ` near ${where}` : ''}`;
}

const CARD_W = 330;

export default function SecurityCard({ m }) {
  const [failed, setFailed] = useState(false);
  const p = useMemo(() => parseSecurity(m._plain), [m._plain]);

  const lat = Number(p?.latitude) || 0;
  const lon = Number(p?.longitude) || 0;
  const hasFix = (lat !== 0 || lon !== 0) && !failed;
  const tiles = useMemo(() => (hasFix ? tilesFor(lat, lon, CARD_W) : []), [lat, lon, hasFix]);

  const where = p ? [p.city, p.region || p.country].filter(Boolean).join(', ') : '';
  const when = p?.at ? new Date(p.at) : null;
  const timeLabel = when
    ? `${when.toLocaleDateString([], { month: 'short', day: 'numeric' })} · ${
      when.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
    : '';

  return (
    <div className="sec-card" style={{ width: CARD_W }}>
      <div className="sec-head">
        <span className="sec-shield"><ShieldCheck width={15} height={15} /></span>
        <span className="sec-title">New login detected</span>
      </div>

      {hasFix ? (
        <div className="sec-map" style={{ height: MAP_H }}>
          {tiles.map((t) => (
            <img
              key={t.key}
              src={t.uri}
              alt=""
              draggable={false}
              style={{ position: 'absolute', left: t.left, top: t.top, width: TILE, height: TILE }}
              onError={() => setFailed(true)}
            />
          ))}
          <span className="sec-pin"><span className="sec-pin-pulse" /><span className="sec-pin-dot" /></span>
          {!!where && <span className="sec-place">near {where}</span>}
          <span className="sec-attrib">© OpenStreetMap</span>
        </div>
      ) : (
        <div className="sec-map sec-nomap" style={{ height: 66 }}>
          <MapPin width={16} height={16} />
          <span>{where ? `near ${where}` : 'Location unavailable'}</span>
        </div>
      )}

      <div className="sec-body">
        <div className="sec-row"><Laptop width={15} height={15} />
          <b>{p?.deviceName || 'A new device'}</b>
        </div>
        {!!timeLabel && <div className="sec-row"><Alert width={15} height={15} /><span>{timeLabel}</span></div>}
        <div className="sec-row"><KeyRound width={15} height={15} />
          <span>{METHOD_LINE[p?.method] || 'Approved by you'}</span>
        </div>
      </div>
    </div>
  );
}
